/* eslint-disable class-methods-use-this */
import * as analytics from 'analytics';
import { isChrome, isIE11andLower } from 'util/device';
import { HLS_SCRIPT_SRC } from 'players/Players.const';
import Timer from './Timer';
import logger from 'util/logger';

const HEALTHY_PLAY_DURATION = 30 * 1000;
const HEALTHY_PLAY_CHECK_INTERVAL = 1000;

export default class AudioPlayer {
  /**
   * Global user setting determining whether we should try to retrieve HD or SD
   * sources
   * @static
   * @type {Boolean}
   */
  static prefersHD = false;

  timeDiffUpdate = isIE11andLower ? 1 : 0.5;

  constructor() {
    this.audioElement = new window.Audio();
    this.playSessionId = null;
    this.healthyPlayDetector = null;

    this.errorCallback = () => {
      throw new Error('Error callback must be set');
    };
    this.stopCallback = () => {
      throw new Error('Stop callback must be set');
    };
    this.playCallback = () => {
      throw new Error('Play callback must be set');
    };
    this.audioElement.onpause = () => {
      analytics.stopHeartBeat();
      this.stopCallback();
    };

    this.audioElement.addEventListener('playing', () => {
      analytics.stopHeartBeat();

      if (this.station || this.onDemandClip) analytics.startHeartBeat();
      this.playCallback();
    });
  }

  /**
   * Instance getter for static property "preferHD"
   */
  get prefersHD() {
    return AudioPlayer.preferHD;
  }

  /**
   * Instance setter for static property "preferHD"
   * @param {Boolean} useHD
   */
  set prefersHD(useHD) {
    if (typeof useHD !== 'boolean') {
      throw new TypeError('Failed setting static property "preferHD". Incorrect type. Expected a boolean.');
    }

    AudioPlayer.preferHD = useHD;
  }

  loadHls = () =>
    new Promise((resolve) => {
      if (window.Hls && this.hls) {
        resolve();
        return;
      }

      const script = document.createElement('SCRIPT');

      script.src = HLS_SCRIPT_SRC;
      script.async = false;

      script.onload = () => {
        this.hls = window.Hls.isSupported() ? new window.Hls() : null;
        resolve();
      };

      script.onerror = () => {
        resolve();
      };

      document.getElementsByTagName('HEAD')[0].appendChild(script);
    });

  /**
   * Uses the experimental Android/Chrome Media Session API to set the meta data
   * for the lock screen
   *
   * @param {string} title
   * @param {string} imageURL
   */
  setMediaSessionData = (title, imageURL) => {
    if (!title) {
      return;
    }

    if ('mediaSession' in navigator) {
      const config = {
        title,
      };

      if (imageURL) {
        config.artwork = {
          src: imageURL,
        };
      }

      navigator.mediaSession.metadata = new MediaMetadata({ config }); //eslint-disable-line
    }
  };

  /**
   * Sets the error callback to be used by the player
   *
   * @param {function} callback
   */
  setErrorCallback(callback) {
    this.errorCallback = callback;
  }

  /**
   * Sets the play callback to be used by the player
   *
   * @param {function} callback
   */
  setPlayCallback(callback) {
    this.playCallback = callback;
  }

  /**
   * Sets the stop callback to be used by the player
   *
   * @param {function} callback
   */
  setStopCallback(callback) {
    this.stopCallback = callback;
  }

  /**
   * Given a callback, binds it to a Timer with a given duration and interval
   *
   * @param {function} healthyPlayCallback
   */
  trackHealthyPlay(healthyPlayCallback) {
    this.healthyPlayDetector = new Timer(HEALTHY_PLAY_DURATION, HEALTHY_PLAY_CHECK_INTERVAL, () =>
      healthyPlayCallback(this.playSessionId)
    );
  }

  /**
   * Binds the given callback to the audioElement's timeupdate event
   * @param {function} callback
   */
  timeUpdate(callback) {
    // Lower the amount of dispatches to every second
    this.audioElement.addEventListener(
      'timeupdate',
      (event) => {
        const { currentTime } = event.target;

        if (Math.abs(this.currentTime - currentTime) > this.timeDiffUpdate) {
          this.currentTime = currentTime;
          callback(currentTime);
        }
      },
      true
    );
  }

  /**
   * Given an audioStringSource, initialises a HTML5 audio object based on source's encoding
   * @param {string|string[]} audioStringSource
   */
  async initialisePlayer(audioStreamSource) {
    this.audioElement.pause();
    this.audioElement.innerHTML = '';
    this.audioElement.removeAttribute('src');

    if (typeof audioStreamSource === 'string') {
      this.audioElement.src = audioStreamSource;
    } else {
      // Prepare sources and sort based on browser/device playback support
      const sources = Object.entries(audioStreamSource).sort((a, b) => {
        // The playType 'probably' is more likely to be supported
        const canPlayTypeA = this.audioElement.canPlayType(a[0]) === 'probably';
        const canPlayTypeB = this.audioElement.canPlayType(b[0]) === 'probably';

        if (canPlayTypeA && !canPlayTypeB) {
          return -1;
        }
        if (!canPlayTypeA && canPlayTypeB) {
          return 1;
        }
        return 0;
      });

      if (sources.some(([key]) => key === 'application/x-mpegurl')) {
        await this.loadHls();
      }

      let errorCount = 0;
      sources.forEach(([key, value]) => {
        if (key === 'application/x-mpegurl' && window.Hls && window.Hls.isSupported()) {
          this.hls.loadSource(value);
          this.hls.attachMedia(this.audioElement);
        }

        if (this.audioElement.canPlayType && this.audioElement.canPlayType(key)) {
          const source = document.createElement('source');
          source.type = key;
          source.src = value;

          source.onerror = () => {
            errorCount += 1;
            const error =
              value.includes('http://') && isChrome()
                ? {
                    name: 'MixedContent',
                    message: 'http source is used',
                  }
                : {
                    name: null,
                    message: 'Source error',
                  };

            logger.error(`One of the audioElement sources errored: ${source.src} - ${error.message}`);

            if (errorCount === sources.length) {
              this.errorCallback(error, this.station || this.onDemandClip, this.playSessionId);
            }
          };

          this.audioElement.appendChild(source);
        }
      });
    }
    this.audioElement.load();
  }

  /**
   * Stops the audio playback
   */
  stop() {
    this.healthyPlayDetector.pause();
    this.audioElement.pause();
  }

  /**
   * Sets the playback volume based on the given volumeLevel
   *
   * @param {number} volumeLevel
   */
  setVolumeLevel(volumeLevel) {
    this.audioElement.volume = volumeLevel;
  }

  /**
   * Mutes/Unmutes playback based on given muted
   *
   * @param {boolean} muted
   */
  setMuted(muted) {
    if (this.audioElement.muted === muted) {
      return;
    }

    this.audioElement.muted = muted;

    if (muted) {
      this.healthyPlayDetector.pause();
    } else {
      this.healthyPlayDetector.resume();
    }
  }

  /**
   * Binds the given callback to the audioElement's ended event
   * @param {function} callback
   */
  endedPlay(callback) {
    this.audioElement.addEventListener('ended', () => {
      callback();
    });
  }

  /**
   * Unimplemented play function (scaffold for inheriting classes)
   * @abstract
   */
  async play() {
    throw new Error(`must be implemented by subclass ${this.playSessionId}`);
  }

  /**
   * Unimplemented resume function (scaffold for inheriting classes)
   * @abstract
   */
  async resume() {
    throw new Error(`must be implemented by subclass ${this.playSessionId}`);
  }

  /**
   * Unimplemented load function (scaffold for inheriting classes)
   * @abstract
   */
  async load() {
    throw new Error(`must be implemented by subclass ${this.playSessionId}`);
  }
}
